import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "@bcc/core-renderers/styles.css";
import {
	ComponentTypes,
	type All8PageComponent,
	type All8PageContent
} from "@bcc/core-renderers";
import { client } from "../Clients";
import type { ContentDocument, ContentTypeConfig, ContentViewModel } from "../API/Models";
import { usePageHeader } from "../hooks/usePageHeader";
import { useDeploymentLinks } from "../hooks/useDeploymentLinks";
import { createRuntimeImageComponent, resolveStorageUrl } from "../Helper/storage";
import { reorderList } from "../Components/LayoutEditor/utils";
import { showSuccessModal } from "../Helper/showSuccessModal";
import AIAnalyzeModal from "../Components/AIAnalyzeModal";
import ContentVersionsPanel from "../Components/ContentVersions/ContentVersionsPanel";
import type { ContentVersion } from "../Components/ContentVersions/contentVersionsClient";
import PageEditorLayout, { type ContentViewMode } from "../Components/PageEditor/PageEditorLayout";
import ReachabilityModal from "../Components/Content/ReachabilityModal";
import { getContentById } from "../Components/PageEditor/contentByIdClient";
import { useRealtimeEvent } from "../Realtime/RealtimeProvider";
import { usePermissions } from "../Auth/PermissionsContext";
import { toast } from "react-toastify";
import {
	PREVIEW_MAX_WIDTH,
	PREVIEW_VIEWPORTS,
	type PreviewViewportKey
} from "../Components/PageEditor/PageEditorPreviewPanel";
import type { AdminPage, PageMetadata } from "../Components/PageEditor/types";
import {
	attachComponents,
	buildDraftFromContentDoc,
	getComponentRuntimeKey,
	normalizeComponents,
	parsePagePayload,
	previewFingerprint,
	safeDecodeSlug,
	serializePagePayload,
	setValueAtPath
} from "../Components/PageEditor/pageEditorUtils";
import {
	duplicateComponentAt,
	resolveInsertionIndex
} from "../Components/PageEditor/componentListUtils";
import { pageToEditableMarkdown } from "../Components/PageEditor/pageToEditableMarkdown";
import { editableMarkdownToPage } from "../Components/PageEditor/editableMarkdownToPage";
import {
	componentDefinitionMap,
	createComponentInstance,
	type ComponentDefinition,
	type Path
} from "./pageComponentLibrary";
import { useComponentLibraryPicker } from "../Components/PageEditor/useComponentLibraryPicker";
import { useComponentHistory } from "../Components/PageEditor/useComponentHistory";
import { wrapComponentInContainer, unwrapContainerAt } from "../Components/PageEditor/containerOps";
import type { IApplicationState } from "../Redux/Reducers";
import { useVariableFormatting } from "../Components/PageEditor/useVariableFormatting";
import { useBreadcrumbs } from "../Components/PageEditor/useBreadcrumbs";
import { useDynamicContentResolvers } from "../Components/PageEditor/useDynamicContentResolvers";
import {
	collectCardFragmentKeys,
	loadBlockFragments,
	loadCardFragments,
	overlayBlockFragments,
	overlayCardFragments,
	type BlockFragmentMap,
	type CardFragmentMap
} from "../Components/PageEditor/blockFragmentPreview";
import { usePagePersistence } from "../Components/PageEditor/usePagePersistence";
import PageEditorModals from "../Components/PageEditor/PageEditorModals";
import { EditLockBanner } from "../Realtime/useEditLock";
import { EditorPresence } from "../Realtime/EditorPresence";
import { TicketCreateModal } from "../Components/Tickets/TicketCreateModal";
import { LinkedTicketsBanner } from "../Components/Content/LinkedTicketsBanner";
import { EditorAuthorshipBanner } from "../Components/Content/EditorAuthorshipBanner";
import { deriveContentStatus } from "../Components/ContentStatusPill";
import EditorCommitActions from "../Components/Content/EditorCommitActions";

type RouteParams = {
	lang?: string;
	slug?: string;
	id?: string;
};

const DEFAULT_LANGUAGE = "fr";
const PAGE_SCHEMA_KEY = "page";

/**
 * Collect the distinct dynamic references (content type keys or dynamic-page template ids) used by
 * every component of one type that runs in dynamic mode. All dynamic-capable components share the
 * `{ mode, dynamicComponent: { contentTypeKey, dynamicPageId } }` shape; MarketsBoard nests it under
 * `content.inflation`, hence the `selectBlock` hook. The loader effects fetch one batch per key.
 */
const collectDynamicRefs = (
	components: All8PageContent,
	componentType: All8PageComponent["componentType"],
	field: "contentTypeKey" | "dynamicPageId",
	selectBlock: (content: Record<string, any>) => Record<string, any> | undefined = (content) => content
): string[] => {
	const values = new Set<string>();
	components.forEach((component) => {
		if (component.componentType !== componentType) return;
		const block = selectBlock((component.content ?? {}) as Record<string, any>);
		if (!block || block.mode !== "dynamic") return;
		const value = (block.dynamicComponent?.[field] ?? "").toString().trim();
		if (value) values.add(value);
	});
	return Array.from(values);
};

/**
 * Fetch every document of a content type and keep only what the public site would render: not
 * deleted, and either language-less (legacy rows) or matching the page's language. All the dynamic
 * preview loaders share this filter so the editor preview and the deployed output agree.
 */
const fetchPublishedItems = async (contentTypeKey: string, language: string): Promise<ContentDocument[]> => {
	const response = await client.content.apiContentSchemaKeyGet(contentTypeKey);
	return (response.data ?? []).filter(
		(item) => !item?.isDeleted && (!item?.language || item.language === language)
	);
};

/**
 * Full-page editor for "page" documents (routes: /page/:id, /page/:lang/:slug and /new-page).
 *
 * Data flow: the document itself is loaded/saved through usePagePersistence (GET /api/content/by-id
 * when routed by id : the fast path : then GET/PUT /api/content/page/{lang}?slug=…); dynamic-mode
 * components fetch their candidate records via GET /api/content/{schemaKey}; per-page deployment
 * status comes from GET /api/deployment/deployment-queue?target=preview|production; computed
 * dashboard widgets are overlaid from the baked CDN block-fragments (see blockFragments below).
 *
 * The invariant that shapes this file: `draft` (AdminPage) is the single source of truth, and the
 * three content views : blocks, raw JSON, editable markdown : are projections of it that get
 * re-seeded or flushed when the user switches modes or pages (see the view-mode sync effect). The
 * preview column is a pure function of the draft plus the dynamic/fragment overlays, so it stays
 * live in every mode.
 */
const PageEditor: React.FC = () => {
	const params = useParams<RouteParams>();
	const lang = params.lang;
	const routeSlug = params.slug;
	const routeId = params.id;

	const slug = useMemo(() => {
		if (!routeSlug) return undefined;
		const decoded = safeDecodeSlug(routeSlug);
		return decoded ?? routeSlug;
	}, [routeSlug]);

	const id = useMemo(() => {
		if (!routeId) return undefined;
		const decoded = safeDecodeSlug(routeId);
		return decoded ?? routeId;
	}, [routeId]);
	const history = useHistory();
	const location = useLocation();
	const isNewDocument = location.pathname === "/new-page";
	const [reloadCount, setReloadCount] = useState(0);
	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const prefix = searchParams.get("prefix") ?? "";
	const slugFromQuery = searchParams.get("slug") ?? "";
	const initialLanguage = (searchParams.get("lang") ?? lang ?? DEFAULT_LANGUAGE).toLowerCase();

	const [draft, setDraft] = useState<AdminPage | null>(null);
	const [showPageAnalyze, setShowPageAnalyze] = useState(false);
	const [showSlugChange, setShowSlugChange] = useState(false);
	const [slugChangeValue, setSlugChangeValue] = useState("");
	const [slugChangeError, setSlugChangeError] = useState<string | null>(null);
	const [changingSlug, setChangingSlug] = useState(false);
	const [connectionsOpen, setConnectionsOpen] = useState(false);
	const [reachabilityOpen, setReachabilityOpen] = useState(false);
	const [showLibrary, setShowLibrary] = useState(false);
	// Insertion target chosen via a « + » gap between cards; null = follow the active-card rule.
	const [libraryInsertIndex, setLibraryInsertIndex] = useState<number | null>(null);
	const [metaOpen, setMetaOpen] = useState(false);
	const [activeComponentKey, setActiveComponentKey] = useState<string | null>(null);
	const [contentViewMode, setContentViewMode] = useState<ContentViewMode>("blocks");
	// Real deployed dev/prod paths for this page (by stable content id) : feeds the links panel.
	// Set when an earlier version was loaded into the draft (local revert) : drives the banner.
	const [loadedVersion, setLoadedVersion] = useState<ContentVersion | null>(null);
	const [pageJsonDraft, setPageJsonDraft] = useState<string>("");
	const [pageJsonError, setPageJsonError] = useState<string | null>(null);
	const [markdownDraft, setMarkdownDraft] = useState<string>("");
	const [markdownError, setMarkdownError] = useState<string | null>(null);
	const lastDraftFromMarkdownRef = useRef<AdminPage | null>(null);
	const lastMarkdownPageKeyRef = useRef<string>("");
	const markdownPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const markdownDraftRef = useRef<string>("");
	const draftRef = useRef<AdminPage | null>(draft);
	draftRef.current = draft;
	const MARKDOWN_PREVIEW_DEBOUNCE_MS = 800;
	const [previewViewport, setPreviewViewport] = useState<PreviewViewportKey>("full");
	const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null);
	const previewContainerRef = useRef<HTMLDivElement | null>(null);
	const [showDynamicPreview, setShowDynamicPreview] = useState(false);
	const [dynamicPreviewItems, setDynamicPreviewItems] = useState<ContentDocument[]>([]);
	const [dynamicPreviewLoading, setDynamicPreviewLoading] = useState(false);
	const [dynamicPreviewError, setDynamicPreviewError] = useState<string | null>(null);
	const [dynamicPreviewSearch, setDynamicPreviewSearch] = useState("");
	const [dynamicPreviewSelection, setDynamicPreviewSelection] = useState<ContentDocument | null>(null);
	const [newsSectionItemsByType, setNewsSectionItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [newsSectionErrors, setNewsSectionErrors] = useState<Record<string, string>>({});
	const [newsSectionLoading, setNewsSectionLoading] = useState<Record<string, boolean>>({});
	const [newsSectionDynamicPages, setNewsSectionDynamicPages] = useState<Record<string, ContentDocument>>({});
	const [paginatedActivityItemsByType, setPaginatedActivityItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [paginatedActivityErrors, setPaginatedActivityErrors] = useState<Record<string, string>>({});
	const [paginatedActivityLoading, setPaginatedActivityLoading] = useState<Record<string, boolean>>({});
	const [latestArticleItemsByType, setLatestArticleItemsByType] = useState<Record<string, ContentDocument[]>>({});
	const [marketsBoardInflationItemsByType, setMarketsBoardInflationItemsByType] = useState<Record<string, ContentDocument[]>>({});
	// Baked block-fragments (preview env) so the page preview renders the home dashboard widgets : // MarketsBoard cards, FxRatesExplorer, InformationHub, governors : from the SAME CDN files the live
	// site reads, matching deployment. This overlay is the source of truth for computed widgets in the
	// preview; it is applied LAST in `previewComponents` so it supersedes any per-widget projection.
	const [blockFragments, setBlockFragments] = useState<BlockFragmentMap>({});
	const [cardFragments, setCardFragments] = useState<CardFragmentMap>({});
	const [deploymentQueuePreview, setDeploymentQueuePreview] = useState<import("../API/Models").DeploymentQueueItem[]>([]);
	const [deploymentQueueProduction, setDeploymentQueueProduction] = useState<import("../API/Models").DeploymentQueueItem[]>([]);
	const [deploymentQueueLoading, setDeploymentQueueLoading] = useState(false);
	const [deploymentQueueRefresh, setDeploymentQueueRefresh] = useState(0);
	// « Publier en production » in flight (owned by the sticky bar's button) → pill « Publication… ».
	const [publishingProduction, setPublishingProduction] = useState(false);
	const viewModeRef = useRef<ContentViewMode>(contentViewMode);
	const lastJsonPageKeyRef = useRef<string>("");
	const [pageNavOpen, setPageNavOpen] = useState(false);

	const setDraftWithComponents = useCallback((next: AdminPage | null) => setDraft(attachComponents(next)), []);

	// Load an earlier version into the draft (LOCAL revert): rebuild the draft from the snapshot's
	// data through the same choke-point the editor uses everywhere, surface the banner, and close the
	// drawer. The existing fingerprint dirty-check flags it; the operator still has to Save.
	const handleLoadVersion = useCallback(
		(data: Record<string, any>, version: ContentVersion) => {
			const language = (draft?.language ?? initialLanguage ?? "fr").toString();
			const doc = { id: draft?.id ?? null, language, data } as ContentDocument;
			const nextDraft = buildDraftFromContentDoc(doc, language, DEFAULT_LANGUAGE);
			if (nextDraft) {
				setDraftWithComponents(nextDraft);
				setLoadedVersion(version);
			}
		},
		[draft?.id, draft?.language, initialLanguage, setDraftWithComponents]
	);

	// Restore the live/current content into the editor (same reload path as the realtime hot-reload)
	// and drop the loaded-version banner.
	const handleReturnToCurrentVersion = useCallback(async () => {
		const id = draft?.id;
		if (!id) return;
		try {
			const doc = await getContentById(id);
			if (!doc) return;
			const language = (draft?.language ?? initialLanguage ?? DEFAULT_LANGUAGE).toString();
			const nextDraft = buildDraftFromContentDoc(doc, language, DEFAULT_LANGUAGE);
			if (nextDraft) {
				setDraftWithComponents(nextDraft);
				setLoadedVersion(null);
			}
		} catch {
			/* transient fetch error : keep the current draft */
		}
	}, [draft?.id, draft?.language, initialLanguage, setDraftWithComponents]);

	// Realtime hot-reload: when ANOTHER admin saves THIS page, pull the fresh data and feed it through
	// the same choke-point (data only, not a full page reload). The session history keeps the pre-reload
	// draft as an undo point (useComponentHistory records the change; an identical reload is a no-op via
	// its structural-equality guard). We ignore our OWN save echo (isMe) and repeats of the same hash.
	const { isMe } = usePermissions();
	const [showTicket, setShowTicket] = useState(false);
	const appliedSaveHashRef = useRef<string | null>(null);
	useRealtimeEvent("content-saved", (evt: any) => {
		const id = draft?.id;
		if (!id || evt?.id !== id) return;
		if (isMe(evt?.user)) return; // echo of my own save
		if (evt?.dataHash && evt.dataHash === appliedSaveHashRef.current) return; // already applied
		appliedSaveHashRef.current = evt?.dataHash ?? null;
		(async () => {
			try {
				const doc = await getContentById(id);
				if (!doc) return;
				const language = (draft?.language ?? initialLanguage ?? DEFAULT_LANGUAGE).toString();
				const nextDraft = buildDraftFromContentDoc(doc, language, DEFAULT_LANGUAGE);
				if (nextDraft) {
					setDraftWithComponents(nextDraft);
					const who = evt?.user && evt.user !== "SYSTEM" ? evt.user : "un autre utilisateur";
					toast.info(`Page mise à jour par ${who} : données rechargées. Utilisez « Annuler » pour revenir à votre version.`);
				}
			} catch {
				/* transient fetch error : keep the current draft */
			}
		})();
	});

	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);
	const categoryKeyMap = useMemo(() => {
		const map = new Map<string, string>();
		categories.forEach((category) => {
			if (category.id) {
				map.set(category.id, category.key ?? category.title ?? category.id);
			}
		});
		return map;
	}, [categories]);
	const categoryTitleMap = useMemo(() => {
		const map = new Map<string, string>();
		categories.forEach((category) => {
			if (category.id) {
				map.set(category.id, category.title ?? category.key ?? category.id);
			}
		});
		return map;
	}, [categories]);
	const dynamicContentTypeConfig = useMemo<ContentTypeConfig | null>(
		() => contentTypeConfigs.find((config) => config.id === draft?.dynamicContentTypeId) ?? null,
		[contentTypeConfigs, draft?.dynamicContentTypeId]
	);
	const basePreviewComponents = useMemo<All8PageContent>(() => normalizeComponents(draft?.components), [draft?.components]);
	// Index of the card currently open in the components panel (runtime keys are index-derived).
	const activeComponentIndex = useMemo(() => {
		if (!activeComponentKey) return null;
		const index = basePreviewComponents.findIndex(
			(component, i) => getComponentRuntimeKey(component.componentType as string, i) === activeComponentKey
		);
		return index >= 0 ? index : null;
	}, [activeComponentKey, basePreviewComponents]);
	const newsSectionContentTypes = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.NewsSection, "contentTypeKey"),
		[basePreviewComponents]
	);
	const paginatedActivityContentTypes = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.PaginatedActivityGrid, "contentTypeKey"),
		[basePreviewComponents]
	);
	const latestArticleContentTypes = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.LatestArticleGrid, "contentTypeKey"),
		[basePreviewComponents]
	);
	const marketsBoardInflationContentTypes = useMemo(
		() =>
			collectDynamicRefs(
				basePreviewComponents,
				ComponentTypes.MarketsBoard,
				"contentTypeKey",
				(content) => content.inflation
			),
		[basePreviewComponents]
	);
	const newsSectionDynamicPageIds = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.NewsSection, "dynamicPageId"),
		[basePreviewComponents]
	);
	const paginatedActivityDynamicPageIds = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.PaginatedActivityGrid, "dynamicPageId"),
		[basePreviewComponents]
	);
	const latestArticleDynamicPageIds = useMemo(
		() => collectDynamicRefs(basePreviewComponents, ComponentTypes.LatestArticleGrid, "dynamicPageId"),
		[basePreviewComponents]
	);
	const dynamicPageTemplateIds = useMemo(
		() => Array.from(new Set([...newsSectionDynamicPageIds, ...paginatedActivityDynamicPageIds, ...latestArticleDynamicPageIds])),
		[newsSectionDynamicPageIds, paginatedActivityDynamicPageIds, latestArticleDynamicPageIds]
	);

	// --- Custom hooks ---
	const {
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		formatVariableValue
	} = useVariableFormatting();

	const {
		buildBreadcrumbsFromCategoryPathIds,
		buildBreadcrumbsFromKeyPath,
		applyArticleHeroBreadcrumbs
	} = useBreadcrumbs(categories, categoryKeyMap, categoryTitleMap);

	const {
		resolveNewsSectionDynamicContent,
		resolvePaginatedActivityGridDynamicContent,
		resolveLatestArticleGridDynamicContent,
		resolveMarketsBoardDynamicContent,
		buildDynamicPreviewComponents
	} = useDynamicContentResolvers({
		taxonomyMap,
		categoryKeyMap,
		categoryTitleMap,
		contentTypeConfigs,
		draftLanguage: draft?.language,
		newsSectionItemsByType,
		newsSectionDynamicPages,
		paginatedActivityItemsByType,
		latestArticleItemsByType,
		marketsBoardInflationItemsByType,
		formatVariableValue,
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		applyArticleHeroBreadcrumbs
	});

	const persistence = usePagePersistence({
		routeId,
		routeSlug,
		slugFromQuery,
		prefix,
		initialLanguage,
		lang,
		slug,
		id,
		isNewDocument,
		reloadCount,
		setDraftWithComponents,
		draft
	});

	const {
		view, setView,
		loadingView,
		saving,
		error, setError,
		message, setMessage,
		deleting,
		restoring,
		isEditing, setIsEditing,
		loadPageView,
		persist,
		reloadCurrentPage,
		performDelete,
		handleRestore,
		handleDelete,
		showDeleteConfirm, setShowDeleteConfirm
	} = persistence;

	// --- Preview components ---
	const previewComponents = useMemo<All8PageContent>(() => {
		const base =
			draft?.dynamicContentTypeId && dynamicPreviewSelection
				? buildDynamicPreviewComponents(basePreviewComponents, dynamicPreviewSelection, dynamicContentTypeConfig)
				: basePreviewComponents;
		return base.map((rawComponent) => {
			let component = rawComponent;
			if (component.componentType === ComponentTypes.NewsSection) {
				component = { ...component, content: resolveNewsSectionDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.PaginatedActivityGrid) {
				component = { ...component, content: resolvePaginatedActivityGridDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.LatestArticleGrid) {
				component = { ...component, content: resolveLatestArticleGridDynamicContent(component) };
			} else if (component.componentType === ComponentTypes.MarketsBoard) {
				component = { ...component, content: resolveMarketsBoardDynamicContent(component) };
			}
			// Overlay the baked block-fragments LAST so every computed dashboard widget renders its live
			// CDN data exactly as it will deploy: MarketsBoard cards (cours de change / inflation / taux
			// d'intérêt), the FxRatesExplorer converter, the InformationHub and the governors roster.
			// Then the card-array fragments (latest-articles, latest-{type}) fill NewsSection /
			// LatestArticleGrid the same way the live site does. Non-widget components pass through.
			component = overlayBlockFragments(component, blockFragments);
			return overlayCardFragments(component, cardFragments);
		});
	}, [
		draft?.dynamicContentTypeId,
		dynamicPreviewSelection,
		basePreviewComponents,
		buildDynamicPreviewComponents,
		dynamicContentTypeConfig,
		resolveNewsSectionDynamicContent,
		resolvePaginatedActivityGridDynamicContent,
		resolveLatestArticleGridDynamicContent,
		resolveMarketsBoardDynamicContent,
		blockFragments,
		cardFragments
	]);

	const dynamicPreviewLabel = useMemo(() => {
		if (!dynamicPreviewSelection) return null;
		const data = dynamicPreviewSelection.data as { title?: string; slug?: string } | undefined;
		return data?.title ?? data?.slug ?? dynamicPreviewSelection.id ?? "Content item";
	}, [dynamicPreviewSelection]);

	const filteredDynamicPreviewItems = useMemo(() => {
		const term = dynamicPreviewSearch.trim().toLowerCase();
		if (!term) return dynamicPreviewItems;
		return dynamicPreviewItems.filter((item) => {
			const data = (item.data ?? {}) as { title?: string; slug?: string };
			const title = (data.title ?? "").toString().toLowerCase();
			const slugValue = (data.slug ?? "").toString().toLowerCase();
			const idValue = (item.id ?? "").toString().toLowerCase();
			return title.includes(term) || slugValue.includes(term) || idValue.includes(term);
		});
	}, [dynamicPreviewItems, dynamicPreviewSearch]);
	const allowDynamicComponents = !!draft?.dynamicContentTypeId;
	// DynamicComponents is a template-only injector : offered only when this
	// page is a dynamic template.
	const excludeLibrary = useCallback(
		(definition: ComponentDefinition) =>
			!allowDynamicComponents && definition.type === ComponentTypes.DynamicComponents,
		[allowDynamicComponents]
	);
	const libraryPicker = useComponentLibraryPicker({ exclude: excludeLibrary });

	const resolvedPath = `/page/${draft?.id || "[id]"}`;
	const { previewPageUrl, productionPageUrl } = useMemo(() => {
		const basePreview = process.env.REACT_APP_PUBLIC_PREVIEW_URL ?? "";
		const baseProduction = process.env.REACT_APP_PUBLIC_PRODUCTION_URL ?? "";
		const slugPart = (draft?.slug ?? "").trim().replace(/^\/+|\/+$/g, "");
		const path = slugPart || "";
		const noTrail = (base: string) => base.replace(/\/$/, "");
		return {
			previewPageUrl: basePreview ? (path ? `${noTrail(basePreview)}/${path}` : noTrail(basePreview)) : "",
			productionPageUrl: baseProduction ? (path ? `${noTrail(baseProduction)}/${path}` : noTrail(baseProduction)) : ""
		};
	}, [draft?.slug]);

	// Memes liens Dev/Prod que l'editeur de contenu, meme rafraichissement automatique en fin
	// de deploiement : une page qu'on vient de creer voit son lien Dev apparaitre sans rechargement.
	const { links: deployLinks } = useDeploymentLinks(draft?.id);

	const isJsonLocked = contentViewMode === "json" && !!pageJsonError;

	// While the JSON tab is closed, keep its buffer mirroring the draft so opening the tab is
	// instant and never shows a stale document. While the tab is open the buffer belongs to the
	// user and is only re-seeded by the view-mode sync effect below.
	useEffect(() => {
		if (contentViewMode !== "json") {
			setPageJsonDraft(serializePagePayload(draft, initialLanguage));
			setPageJsonError(null);
		}
	}, [contentViewMode, draft, initialLanguage]);

	useEffect(() => {
		setDynamicPreviewSelection(null);
	}, [draft?.dynamicContentTypeId]);

	useEffect(() => {
		if (!showDynamicPreview || !draft?.dynamicContentTypeId) return;
		let active = true;
		(async () => {
			setDynamicPreviewLoading(true);
			setDynamicPreviewError(null);
			try {
				const language = (draft.language ?? DEFAULT_LANGUAGE).toLowerCase();
				const items = await fetchPublishedItems(draft.dynamicContentTypeId, language);
				if (active) setDynamicPreviewItems(items);
			} catch (err: any) {
				if (active) setDynamicPreviewError(err?.response?.data ?? err?.message ?? "Failed to load content items.");
			} finally {
				if (active) setDynamicPreviewLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [showDynamicPreview, draft?.dynamicContentTypeId, draft?.language]);

	// One loader effect per dynamic component family (NewsSection, PaginatedActivityGrid,
	// LatestArticleGrid, MarketsBoard inflation). They stay separate on purpose: a key added to one
	// family only refetches that family, and the first two surface per-type loading/error state in
	// the editor while the grid-style widgets just degrade to an empty list.
	useEffect(() => {
		if (newsSectionContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			setNewsSectionLoading((prev) => ({ ...prev, [contentTypeKey]: true }));
			setNewsSectionErrors((prev) => ({ ...prev, [contentTypeKey]: "" }));
			try {
				const items = await fetchPublishedItems(contentTypeKey, language);
				if (active) {
					setNewsSectionItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch (err: any) {
				if (active) {
					setNewsSectionErrors((prev) => ({
						...prev,
						[contentTypeKey]: err?.response?.data ?? err?.message ?? "Failed to load content items."
					}));
				}
			} finally {
				if (active) {
					setNewsSectionLoading((prev) => ({ ...prev, [contentTypeKey]: false }));
				}
			}
		};
		newsSectionContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [newsSectionContentTypes, draft?.language]);

	useEffect(() => {
		if (paginatedActivityContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			setPaginatedActivityLoading((prev) => ({ ...prev, [contentTypeKey]: true }));
			setPaginatedActivityErrors((prev) => ({ ...prev, [contentTypeKey]: "" }));
			try {
				const items = await fetchPublishedItems(contentTypeKey, language);
				if (active) {
					setPaginatedActivityItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch (err: any) {
				if (active) {
					setPaginatedActivityErrors((prev) => ({
						...prev,
						[contentTypeKey]: err?.response?.data ?? err?.message ?? "Failed to load content items."
					}));
				}
			} finally {
				if (active) {
					setPaginatedActivityLoading((prev) => ({ ...prev, [contentTypeKey]: false }));
				}
			}
		};
		paginatedActivityContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [paginatedActivityContentTypes, draft?.language]);

	useEffect(() => {
		if (latestArticleContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			try {
				const items = await fetchPublishedItems(contentTypeKey, language);
				if (active) {
					setLatestArticleItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch {
				if (active) {
					setLatestArticleItemsByType((prev) => ({ ...prev, [contentTypeKey]: [] }));
				}
			}
		};
		latestArticleContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [latestArticleContentTypes, draft?.language]);

	useEffect(() => {
		if (marketsBoardInflationContentTypes.length === 0) return;
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		const loadContentType = async (contentTypeKey: string) => {
			try {
				const items = await fetchPublishedItems(contentTypeKey, language);
				if (active) {
					setMarketsBoardInflationItemsByType((prev) => ({ ...prev, [contentTypeKey]: items }));
				}
			} catch {
				if (active) {
					setMarketsBoardInflationItemsByType((prev) => ({ ...prev, [contentTypeKey]: [] }));
				}
			}
		};
		marketsBoardInflationContentTypes.forEach((key) => {
			if (!key) return;
			loadContentType(key);
		});
		return () => {
			active = false;
		};
	}, [marketsBoardInflationContentTypes, draft?.language]);

	// Load the baked block-fragments (preview env) so the page preview renders every computed dashboard
	// widget : MarketsBoard cards (cours de change / inflation / taux d'intérêt), the FxRatesExplorer
	// converter, the InformationHub and the governors roster : from the SAME CDN files the live site
	// reads. This is what makes the preview match deployment uniformly (no per-widget special case) and
	// auto-align: when a feed re-bakes a fragment, reopening the preview shows the new figures. Re-fetched
	// when the draft language changes; a fragment that isn't baked yet is simply absent (placeholder).
	useEffect(() => {
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		loadBlockFragments("preview", language)
			.then((map) => {
				if (active) setBlockFragments(map);
			})
			.catch(() => {
				if (active) setBlockFragments({});
			});
		return () => {
			active = false;
		};
	}, [draft?.language]);

	// Card-array fragments referenced by THIS page (latest-articles, latest-{type}) : fetched on
	// demand from the same baked files, so NewsSection / article strips preview their real cards.
	const cardFragmentKeys = useMemo(
		() => collectCardFragmentKeys(basePreviewComponents).sort().join(","),
		[basePreviewComponents]
	);
	useEffect(() => {
		if (!cardFragmentKeys) {
			setCardFragments({});
			return;
		}
		let active = true;
		const language = (draft?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		loadCardFragments(cardFragmentKeys.split(","), "preview", language)
			.then((map) => {
				if (active) setCardFragments(map);
			})
			.catch(() => {
				if (active) setCardFragments({});
			});
		return () => {
			active = false;
		};
	}, [cardFragmentKeys, draft?.language]);

	useEffect(() => {
		if (dynamicPageTemplateIds.length === 0) {
			setNewsSectionDynamicPages({});
			return;
		}
		let active = true;
		(async () => {
			try {
				const response = await client.content.apiContentSchemaKeyGet(PAGE_SCHEMA_KEY);
				const docs: ContentDocument[] = response.data ?? [];
				const idSet = new Set(dynamicPageTemplateIds);
				const next: Record<string, ContentDocument> = {};
				docs.forEach((doc) => {
					if (!doc?.id || doc.isDeleted) return;
					if (!idSet.has(doc.id)) return;
					next[doc.id] = doc;
				});
				if (active) setNewsSectionDynamicPages(next);
			} catch {
				if (active) setNewsSectionDynamicPages({});
			}
		})();
		return () => {
			active = false;
		};
	}, [dynamicPageTemplateIds]);

	const loadDeploymentQueue = useCallback(async () => {
		if (!draft?.slug || !draft?.language) return;
		setDeploymentQueueLoading(true);
		try {
			const fetchQueue = async (target: "preview" | "production") => {
				const response = await client.deployment.apiDeploymentDeploymentQueueGet(target, undefined, undefined, {
					withCredentials: true,
					headers: { Accept: "application/json" }
				});
				return Array.isArray(response.data) ? response.data : [];
			};
			const [previewItems, productionItems] = await Promise.all([
				fetchQueue("preview"),
				fetchQueue("production")
			]);
			setDeploymentQueuePreview(previewItems);
			setDeploymentQueueProduction(productionItems);
		} catch (err: any) {
			console.error(err);
			setDeploymentQueuePreview([]);
			setDeploymentQueueProduction([]);
		} finally {
			setDeploymentQueueLoading(false);
		}
	}, [draft?.slug, draft?.language]);

	useEffect(() => {
		if (!draft?.slug || !draft?.language) return;
		loadDeploymentQueue();
	}, [draft?.slug, draft?.language, deploymentQueueRefresh, loadDeploymentQueue]);

	// View-mode synchronization : the trickiest part of the editor. The JSON and markdown buffers
	// are projections of `draft`, so they must be re-seeded when the user ENTERS a mode (or when a
	// different page loads while that mode is open : that's what the pageKey guards detect), and
	// the markdown buffer must be flushed back into the draft when LEAVING markdown so keystrokes
	// typed after the last 800 ms debounce tick are not lost. The flush reads markdownDraftRef
	// rather than markdownDraft state because this effect's closure can predate the latest
	// keystroke (typing doesn't change `draft`, so the effect doesn't re-run); the ref is updated
	// synchronously by handleMarkdownChange. viewModeRef records the previous mode so we can tell
	// entering from staying.
	useEffect(() => {
		const pageKey = `${draft?.id ?? ""}-${draft?.slug ?? ""}-${draft?.language ?? ""}`;
		const enteringJson = contentViewMode === "json" && viewModeRef.current !== "json";
		const pageChangedWhileJson = contentViewMode === "json" && pageKey !== lastJsonPageKeyRef.current;
		if (contentViewMode === "json" && (enteringJson || pageChangedWhileJson)) {
			setPageJsonDraft(serializePagePayload(draft, initialLanguage));
			setPageJsonError(null);
			lastJsonPageKeyRef.current = pageKey;
		}
		if (contentViewMode !== "markdown") {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			if (viewModeRef.current === "markdown") {
				try {
					const latestMd = markdownDraftRef.current;
					const nextDraft = editableMarkdownToPage(latestMd, draft, initialLanguage ?? "fr");
					setDraftWithComponents(nextDraft);
				} catch (_) {
					// ignore parse errors when switching away
				}
			}
		}
		const prevViewMode = viewModeRef.current;
		const enteringMarkdown = contentViewMode === "markdown" && prevViewMode !== "markdown";
		const pageChanged = contentViewMode === "markdown" && pageKey !== lastMarkdownPageKeyRef.current;
		if (contentViewMode === "markdown" && (enteringMarkdown || pageChanged)) {
			const latestDraft = draftRef.current ?? draft;
			const md = pageToEditableMarkdown(latestDraft, initialLanguage ?? "fr");
			markdownDraftRef.current = md;
			setMarkdownDraft(md);
			lastDraftFromMarkdownRef.current = latestDraft;
			lastMarkdownPageKeyRef.current = pageKey;
			setMarkdownError(null);
		}
		viewModeRef.current = contentViewMode;
		return () => {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
		};
	}, [contentViewMode, draft, initialLanguage, setDraftWithComponents]);

	const handleStartEdit = useCallback(() => {
		setIsEditing(true);
		setError(null);
		setMessage(null);
	}, [setIsEditing, setError, setMessage]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setError(null);
		setMessage(null);
		setReloadCount((c) => c + 1);
	}, [setIsEditing, setError, setMessage]);

	const openSlugChange = useCallback(() => {
		if (!draft?.slug || !draft?.language) return;
		setSlugChangeValue(draft.slug);
		setSlugChangeError(null);
		setShowSlugChange(true);
	}, [draft?.slug, draft?.language]);

	const openDynamicPreview = useCallback(() => {
		if (!draft?.dynamicContentTypeId) return;
		setDynamicPreviewError(null);
		setDynamicPreviewSearch("");
		setShowDynamicPreview(true);
	}, [draft?.dynamicContentTypeId]);

	const canEdit = isEditing && !loadingView;

	const normalizedQueueSlug = useMemo(() => (draft?.slug ?? "").trim().toLowerCase(), [draft?.slug]);
	const normalizedQueueLanguage = useMemo(
		() => (draft?.language ?? initialLanguage).toLowerCase(),
		[draft?.language, initialLanguage]
	);
	const previewQueueMatches = useMemo(() => {
		if (!normalizedQueueSlug || !normalizedQueueLanguage) return [];
		return deploymentQueuePreview.filter((item) => {
			const itemType = (item.contentType ?? "").toLowerCase();
			if (itemType !== PAGE_SCHEMA_KEY) return false;
			const itemLang = (item.language ?? "").toLowerCase();
			if (itemLang !== normalizedQueueLanguage) return false;
			const itemSlug = (item.slug ?? "").toLowerCase();
			if (itemSlug === normalizedQueueSlug) return true;
			const pagePaths = Array.isArray(item.pagePaths) ? item.pagePaths : [];
			return pagePaths.some((path) => (path ?? "").toLowerCase() === normalizedQueueSlug);
		});
	}, [deploymentQueuePreview, normalizedQueueLanguage, normalizedQueueSlug]);
	const productionQueueMatches = useMemo(() => {
		if (!normalizedQueueSlug || !normalizedQueueLanguage) return [];
		return deploymentQueueProduction.filter((item) => {
			const itemType = (item.contentType ?? "").toLowerCase();
			if (itemType !== PAGE_SCHEMA_KEY) return false;
			const itemLang = (item.language ?? "").toLowerCase();
			if (itemLang !== normalizedQueueLanguage) return false;
			const itemSlug = (item.slug ?? "").toLowerCase();
			if (itemSlug === normalizedQueueSlug) return true;
			const pagePaths = Array.isArray(item.pagePaths) ? item.pagePaths : [];
			return pagePaths.some((path) => (path ?? "").toLowerCase() === normalizedQueueSlug);
		});
	}, [deploymentQueueProduction, normalizedQueueLanguage, normalizedQueueSlug]);

	const productionStatus = useMemo(() => {
		if (!view?.production) return null;
		const cur = view.current as ContentDocument | undefined;
		return {
			deployedAt: cur?.productionDeployedAt ?? view.production.deployedAt ?? null,
			exists: view.production.exists ?? false
		};
	}, [view]);

	// Simplified publication state for the sticky bar's pill. The preview syncs itself, so the
	// only question is whether PRODUCTION lags: any queue row matching this page (by schema +
	// language + slug, or any of its pagePaths : dynamic templates enqueue under per-record
	// paths) or a save newer than the last production deploy reads « Modifié ».
	const currentDoc = view?.current as ContentDocument | undefined;
	const publishStatus = deriveContentStatus({
		isProductionDeployed: productionStatus?.exists === true,
		updatedAt: currentDoc?.updatedAt ?? null,
		productionDeployedAt: productionStatus?.deployedAt ?? null,
		publishing: publishingProduction
	});
	const pageHasQueuedChanges = previewQueueMatches.length > 0 || productionQueueMatches.length > 0;
	const effectivePublishStatus =
		publishStatus === "live" && pageHasQueuedChanges && !deploymentQueueLoading ? "modified" : publishStatus;

	const pendingDeployMessage =
		effectivePublishStatus === "modified" && !publishingProduction && productionStatus?.exists
			? "Des modifications sont en attente de publication en production."
			: null;

	// Dirty-state compares structural fingerprints of the draft against the last server document : // not object identity : so normalization churn (attachComponents, serialization key order)
	// can't produce false "unsaved changes" warnings.
	const lastSavedFingerprint = useMemo(() => {
		if (!view?.current) return null;
		const saved = buildDraftFromContentDoc(
			view.current as ContentDocument,
			initialLanguage,
			DEFAULT_LANGUAGE
		);
		return saved ? previewFingerprint(saved, initialLanguage) : null;
	}, [view?.current, initialLanguage]);

	const hasUnsavedChanges =
		!!lastSavedFingerprint &&
		previewFingerprint(draft, initialLanguage) !== lastSavedFingerprint;

	const unsavedChangesMessage = hasUnsavedChanges ? "You have unsaved changes." : null;

	const saveBlockerReasons = useMemo(() => {
		if (saving || loadingView) return [];
		const reasons: string[] = [];
		if (!(draft?.slug ?? "").trim()) reasons.push("Slug is missing.");
		if (!(draft?.language ?? "").trim()) reasons.push("Language is required.");
		if (isJsonLocked) reasons.push("Fix JSON errors to save.");
		return reasons;
	}, [draft?.slug, draft?.language, isJsonLocked, saving, loadingView]);

	const connectionsDisabled = !draft?.slug || !draft?.language;
	const saveDisabled = !canEdit || saving || loadingView || isJsonLocked || !draft?.slug || !draft?.language;
	const publishDisabled = saving || loadingView || deleting || !draft?.id || draft?.isDeleted === true;
	const deleteDisabled = deleting || !draft?.id || !draft?.slug || !draft?.language;
	// A page live in production can't be deleted until it's unpublished (a deployment action). An author
	// without the deployment permission can't do that, so hide Delete entirely and leave Dépublier as the
	// only path. The server enforces the same guard (409).
	const pageIsProductionDeployed = productionStatus?.exists === true || (deployLinks?.production?.length ?? 0) > 0;
	const canChangeSlug = canEdit && !!draft?.id;
	const allowImageVariables = !!draft?.dynamicContentTypeId;

	const handleConfirmSlugChange = useCallback(async () => {
		if (!draft?.slug || !draft?.language) return;
		const nextSlug = slugChangeValue.trim();
		if (!nextSlug) {
			setSlugChangeError("New slug is required.");
			return;
		}
		setChangingSlug(true);
		setSlugChangeError(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const response = await client.content.apiContentPageChangeSlugPost(
				{
					language: langCode,
					currentSlug: draft.slug,
					newSlug: nextSlug
				}
			);
			// La réponse porte désormais { view, redirectCreated, descendantCount } ; on tolère
			// l'ancienne forme (le ContentViewModel nu) le temps des rollouts croisés.
			const payload: any = response.data ?? null;
			const vm = (payload && typeof payload === "object" && "view" in payload ? payload.view : payload) ?? null;
			const redirectCreated: boolean = Boolean(payload?.redirectCreated);
			const descendantCount: number = Number(payload?.descendantCount) || 0;
			setView(vm);
			const nextDraft = buildDraftFromContentDoc(vm?.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
			if (nextDraft) {
				setDraftWithComponents(nextDraft);
			}
			setShowSlugChange(false);
			const messageParts = ["Le chemin de la page a été mis à jour et les références réécrites."];
			messageParts.push(
				redirectCreated
					? "Une redirection temporaire (302) de l'ancien chemin vers le nouveau a été créée et publiée."
					: "Aucune redirection automatique n'a pu être créée pour ce renommage."
			);
			if (descendantCount > 0) {
				messageParts.push(
					`${descendantCount} sous-page${descendantCount > 1 ? "s" : ""} reste${descendantCount > 1 ? "nt" : ""} sous l'ancien préfixe : renommez-les aussi si besoin (aucune cascade automatique).`
				);
			}
			showSuccessModal({
				title: "Chemin mis à jour",
				message: messageParts.join(" ")
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? "Slug change failed.";
			setSlugChangeError(String(apiMessage));
		} finally {
			setChangingSlug(false);
		}
	}, [draft?.slug, draft?.language, slugChangeValue, initialLanguage, setDraftWithComponents, setView]);


	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			imageComponent: runtimeImageComponent,
			// Blocks that render plain <img> via the runtime (Gallery) resolve their
			// bucket-relative keys here : without it the preview shows broken images.
			assetResolver: resolveStorageUrl
		}),
		[runtimeImageComponent]
	);
	const previewWidth = PREVIEW_VIEWPORTS[previewViewport];

	// Measure the real preview column so device viewports can be scaled to fit it. The double
	// requestAnimationFrame re-measures after the viewport-switch relayout has settled (a single
	// synchronous read can still see the old width); the ResizeObserver then keeps the measurement
	// current for panel/window resizes.
	useEffect(() => {
		const el = previewContainerRef.current;
		if (!el) return;
		const updateWidth = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setPreviewContainerWidth(w);
		};
		updateWidth();
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			const w = entry?.contentRect?.width ?? 0;
			if (w > 0) setPreviewContainerWidth(w);
		});
		observer.observe(el);
		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(updateWidth);
		});
		return () => {
			observer.disconnect();
			cancelAnimationFrame(raf);
		};
	}, [previewViewport]);

	// The public renderers size full-bleed sections against the actual viewport
	// (mx-[calc(50%-50vw)]); inside the editor that would overflow the preview column, so this
	// scoped CSS re-centers those sections and caps everything at the preview width.
	const previewCss = `
    .page-editor-preview {
      max-width: ${PREVIEW_MAX_WIDTH}px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }
    .page-editor-preview section {
      margin-left: 0 !important;
      margin-right: 0 !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
    }
    .page-editor-preview section[class*="mx-[calc(50%-50vw)"] {
      margin-left: auto !important;
      margin-right: auto !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
      width: 100% !important;
    }
    .page-editor-preview section .max-w-7xl {
      max-width: ${PREVIEW_MAX_WIDTH}px;
    }
  `;

	const jsonEditorOptions = useMemo(
		() => ({
			minimap: { enabled: false },
			lineNumbers: "on" as const,
			folding: true,
			showFoldingControls: "always" as const,
			formatOnPaste: true,
			formatOnType: true,
			wordWrap: "on" as const,
			scrollBeyondLastLine: false,
			automaticLayout: true,
			tabSize: 2
		}),
		[]
	);

	const updateComponentValue = useCallback(
		(index: number, path: Path, value: any) => {
			if (!canEdit) return;
			setDraft((prev) => {
				if (!prev) return prev;
				const components = normalizeComponents(prev.components);
				if (!components[index]) return prev;
				let updatedComponent = setValueAtPath(components[index], path, value);
				if (updatedComponent.componentType === ComponentTypes.ArticleHero) {
					const content = (updatedComponent.content ?? {}) as Record<string, any>;
					const source = (content.breadcrumbsSource ?? "manual").toString();
					if (source === "categories") {
						const keyPath = (content.breadcrumbsCategoryPath ?? "").toString().trim();
						const breadcrumbs = keyPath
							? buildBreadcrumbsFromKeyPath(keyPath, content.breadcrumbsLink)
							: buildBreadcrumbsFromCategoryPathIds(
									Array.isArray(content.breadcrumbsCategoryPathIds)
										? content.breadcrumbsCategoryPathIds.map(String)
										: [],
									content.breadcrumbsLink
							  );
						updatedComponent = {
							...updatedComponent,
							content: {
								...content,
								breadcrumbs: breadcrumbs ?? []
							}
						};
					} else if (source === "dynamic") {
						updatedComponent = {
							...updatedComponent,
							content: { ...content, breadcrumbs: [] }
						};
					}
				}
				const nextComponents = [...components];
				nextComponents[index] = updatedComponent;
				return { ...prev, components: nextComponents };
			});
		},
		[buildBreadcrumbsFromCategoryPathIds, buildBreadcrumbsFromKeyPath, canEdit, setDraft]
	);

	const handlePageJsonChange = useCallback(
		(nextValue: string) => {
			if (!canEdit) return;
			setPageJsonDraft(nextValue);
			try {
				const nextDraft = parsePagePayload(nextValue, draft, initialLanguage);
				setDraftWithComponents(nextDraft);
				setPageJsonError(null);
			} catch {
				setPageJsonError("JSON not valid.");
			}
		},
		[canEdit, draft, initialLanguage, setDraftWithComponents]
	);

	const handleFormatJson = useCallback(() => {
		try {
			const formatted = JSON.stringify(JSON.parse(pageJsonDraft), null, 2);
			handlePageJsonChange(formatted);
		} catch {
			setPageJsonError("JSON not valid.");
		}
	}, [pageJsonDraft, handlePageJsonChange]);

	// Markdown edits update the ref synchronously (that's what the mode-switch flush reads) but the
	// draft only after the debounce settles : the markdown→page parse is too heavy to run per
	// keystroke, and a half-typed document would flash parse errors in the preview.
	const handleMarkdownChange = useCallback(
		(nextValue: string) => {
			if (!canEdit) return;
			markdownDraftRef.current = nextValue;
			setMarkdownDraft(nextValue);
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			markdownPreviewDebounceRef.current = setTimeout(() => {
				markdownPreviewDebounceRef.current = null;
				try {
					const nextDraft = editableMarkdownToPage(nextValue, draft, initialLanguage ?? "fr");
					setDraftWithComponents(nextDraft);
					lastDraftFromMarkdownRef.current = nextDraft;
					setMarkdownError(null);
				} catch (err) {
					setMarkdownError(err instanceof Error ? err.message : "Markdown invalid.");
				}
			}, MARKDOWN_PREVIEW_DEBOUNCE_MS);
		},
		[canEdit, draft, initialLanguage, setDraftWithComponents]
	);

	const removeComponent = useCallback((index: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponents = components.filter((_, i) => i !== index);
			return { ...prev, components: nextComponents };
		});
	}, [canEdit, setDraft]);

	const moveComponent = useCallback((from: number, to: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponents = reorderList(components, from, to);
			return { ...prev, components: nextComponents };
		});
	}, [canEdit, setDraft]);

	const duplicateComponent = useCallback((index: number) => {
		if (!canEdit) return;
		const source = basePreviewComponents[index];
		if (!source) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponents = duplicateComponentAt(components, index);
			return nextComponents ? { ...prev, components: nextComponents } : prev;
		});
		// The clone lands right below the original; runtime keys are index-derived, so its key is known.
		setActiveComponentKey(getComponentRuntimeKey(source.componentType as string, index + 1));
	}, [basePreviewComponents, canEdit, setDraft]);

	const wrapInContainer = useCallback((index: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const next = wrapComponentInContainer(components, index);
			return next === components ? prev : { ...prev, components: next };
		});
	}, [canEdit, setDraft]);

	const unwrapContainer = useCallback((index: number) => {
		if (!canEdit) return;
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const next = unwrapContainerAt(components, index);
			return next === components ? prev : { ...prev, components: next };
		});
	}, [canEdit, setDraft]);

	// Session undo/redo over the page's component list (debounced deep-copy
	// snapshots). resetKey clears history when the loaded document changes.
	const historyResetKey = `${draft?.id ?? ""}:${draft?.slug ?? ""}:${draft?.language ?? ""}`;
	const applyHistory = useCallback(
		(comps: All8PageContent) =>
			setDraft((prev) => (prev ? { ...prev, components: normalizeComponents(comps) } : prev)),
		[setDraft]
	);
	const componentHistory = useComponentHistory({
		value: basePreviewComponents,
		apply: applyHistory,
		resetKey: historyResetKey
	});

	const markdownInsertRef = useRef<((def: ComponentDefinition) => void) | null>(null);

	// Selecting a card and bringing it into view always go together: a selection the editor cannot see
	// reads as "nothing happened", which is why adding a block used to send people clicking around the
	// preview to find it. Every selection path goes through here. Selection is pure UI state (no content
	// mutation), so it also works in read-only mode.
	const selectAndRevealComponent = useCallback((componentType: string, index: number) => {
		const key = getComponentRuntimeKey(componentType, index);
		setActiveComponentKey(key);
		// Wait for the accordion to re-render before scrolling the matching card into view.
		requestAnimationFrame(() => {
			const card = document.querySelector(`[data-component-key="${key}"]`) as HTMLElement | null;
			if (card && typeof card.scrollIntoView === "function") {
				card.scrollIntoView({ block: "nearest" });
			}
		});
	}, []);

	const addComponentFromDefinition = useCallback((definition: ComponentDefinition | null, targetIndex?: number | null) => {
		if (!definition || !canEdit) return;
		if (contentViewMode === "markdown" && markdownInsertRef.current) {
			markdownInsertRef.current(definition);
			setShowLibrary(false);
			setLibraryInsertIndex(null);
			return;
		}
		// Explicit target (gap « + ») wins, then "after the active card", then append.
		const insertIndex = resolveInsertionIndex(
			targetIndex ?? libraryInsertIndex,
			activeComponentIndex,
			basePreviewComponents.length
		);
		setDraft((prev) => {
			if (!prev) return prev;
			const components = normalizeComponents(prev.components);
			const nextComponent = createComponentInstance(definition);
			// Loose typing on purpose: the fresh instance has no runtime key yet; normalizeComponents
			// re-derives keys for the whole list on the next read.
			const nextComponents: any[] = [...components];
			nextComponents.splice(Math.min(insertIndex, nextComponents.length), 0, nextComponent);
			return { ...prev, components: nextComponents };
		});
		selectAndRevealComponent(definition.type as string, insertIndex);
		setShowLibrary(false);
		setLibraryInsertIndex(null);
	}, [activeComponentIndex, basePreviewComponents.length, canEdit, contentViewMode, libraryInsertIndex, selectAndRevealComponent, setDraft, setShowLibrary]);

	const openLibraryAtGap = useCallback((gapIndex: number) => {
		if (!canEdit) return;
		setLibraryInsertIndex(gapIndex);
		setShowLibrary(true);
	}, [canEdit, setShowLibrary]);

	// Preview → card linking: clicking a block in the iframe opens its card and brings it into view.
	const handleSelectComponentFromPreview = useCallback((index: number) => {
		const component = basePreviewComponents[index];
		if (!component) return;
		selectAndRevealComponent(component.componentType as string, index);
	}, [basePreviewComponents, selectAndRevealComponent]);

	// A gap target only lives as long as the library stays open (covers close-without-adding).
	useEffect(() => {
		if (!showLibrary) setLibraryInsertIndex(null);
	}, [showLibrary]);

	const pageHeaderTitle = useMemo(() => {
		const t = draft?.title?.trim();
		if (t) return t;
		const components = draft?.components ? normalizeComponents(draft.components) : [];
		const first = components[0];
		if (first?.componentType === ComponentTypes.PageHeader && first?.content && typeof first.content === "object" && first.content !== null) {
			const headerTitle = String((first.content as { title?: string }).title ?? "").trim();
			if (headerTitle) return headerTitle;
		}
		return draft?.slug?.trim() || (slug ? "Edit page" : "New page");
	}, [draft?.title, draft?.slug, draft?.components, slug]);

	// The commit actions live in the app's TOP bar, hard right : same component, same corner as the
	// content-data editor. Everything secondary is one level down in the EditorActionBar.
	usePageHeader({
		title: pageHeaderTitle,
		actions: (
			<EditorCommitActions
				isEditing={isEditing}
				onStartEdit={handleStartEdit}
				editDisabled={loadingView}
				onCancelEdit={handleCancelEdit}
				onSave={async () => {
					const saved = await persist();
					if (!saved) return; // erreur de sauvegarde : on reste en édition
					// The loaded version is now the live one : retire the revert banner.
					setLoadedVersion(null);
					// La session d'édition est terminée : le bouton « Publier » réapparaît
					// sans devoir cliquer Annuler.
					setIsEditing(false);
				}}
				saveDisabled={saveDisabled}
				saving={saving}
				onDelete={handleDelete}
				deleteDisabled={deleteDisabled}
				deleting={deleting}
				contentId={draft?.id ?? null}
				publishDisabled={publishDisabled}
				publishHidden={hasUnsavedChanges}
				isProductionDeployed={pageIsProductionDeployed}
				onPublished={() => {
					reloadCurrentPage();
					setDeploymentQueueRefresh((v) => v + 1);
				}}
				onPublishingChange={setPublishingProduction}
			/>
		)
	});

	// The per-field translation workspace replaced the old whole-document modal.
	const translateHref = useMemo(() => {
		const params = new URLSearchParams();
		if (draft?.slug) params.set("slug", draft.slug);
		params.set("lang", "en");
		return `/traduction/page?${params.toString()}`;
	}, [draft?.slug]);

	return (
		<>
			{/* Soft edit-lock contention warning : only renders when another admin holds this page. */}
			<EditLockBanner contentId={draft?.id} className="mx-4 mt-3 sm:mx-6" />
			{/* Who else is viewing this page right now (presence by path). */}
			<EditorPresence className="mx-4 mt-3 sm:mx-6" />
			{/* « Créer un ticket » used to sit here, alone in its own right-aligned row between two
			    banners. It now lives in the page header actions (see usePageHeader above). */}
			<TicketCreateModal
				open={showTicket}
				onClose={() => setShowTicket(false)}
				linkedContentId={draft?.id ?? null}
				linkedContentType={draft?.dynamicContentTypeId ?? "page"}
				defaultCategory="contenu"
			/>

			{loadedVersion && (
				<div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:mx-6">
					<span className="font-semibold">
						Version du {new Date(loadedVersion.createdAt).toLocaleString("fr-FR")} chargée.
					</span>{" "}
					Enregistrez pour la rétablir, ou rechargez la page pour conserver la version actuelle.
				</div>
			)}

			<PageEditorModals
				showDeleteConfirm={showDeleteConfirm}
				onHideDeleteConfirm={() => setShowDeleteConfirm(false)}
				onConfirmDelete={() => performDelete()}
				deleting={deleting}
				showSlugChange={showSlugChange}
				onHideSlugChange={() => setShowSlugChange(false)}
				slugChangeValue={slugChangeValue}
				onSlugChangeValueChange={setSlugChangeValue}
				slugChangeError={slugChangeError}
				changingSlug={changingSlug}
				onConfirmSlugChange={() => handleConfirmSlugChange()}
				showDynamicPreview={showDynamicPreview}
				onHideDynamicPreview={() => setShowDynamicPreview(false)}
				dynamicPreviewSearch={dynamicPreviewSearch}
				onDynamicPreviewSearchChange={setDynamicPreviewSearch}
				dynamicPreviewError={dynamicPreviewError}
				dynamicPreviewLoading={dynamicPreviewLoading}
				filteredDynamicPreviewItems={filteredDynamicPreviewItems}
				onSelectDynamicPreviewItem={(item) => {
					setDynamicPreviewSelection(item);
					setShowDynamicPreview(false);
				}}
			/>
			<PageEditorLayout
			editable={canEdit}
			error={error}
			message={message}
			unsavedChangesMessage={unsavedChangesMessage}
			saveBlockerReasons={saveBlockerReasons}
			pendingDeployMessage={pendingDeployMessage}
			loadingView={loadingView}
			draft={draft}
			topBannerSlot={
				draft?.id ? (
					<>
						<LinkedTicketsBanner contentId={draft.id} />
						<EditorAuthorshipBanner contentId={draft.id} />
					</>
				) : undefined
			}
			contentViewMode={contentViewMode}
			metaOpen={metaOpen}
			setMetaOpen={setMetaOpen}
			versionsPanel={draft?.id ? <ContentVersionsPanel contentId={draft.id} onLoad={handleLoadVersion} loadedVersionId={loadedVersion?.id ?? null} onReturnToCurrent={handleReturnToCurrentVersion} /> : undefined}
			setDraft={setDraft}
			resolvedPath={resolvedPath}
			canChangeSlug={canChangeSlug}
			onOpenSlugChange={openSlugChange}
			dynamicPreviewLabel={dynamicPreviewLabel}
			onOpenDynamicPreview={draft?.dynamicContentTypeId ? openDynamicPreview : undefined}
			onClearDynamicPreview={() => setDynamicPreviewSelection(null)}
			previewViewport={previewViewport}
			setPreviewViewport={setPreviewViewport}
			previewPageUrl={previewPageUrl}
			productionPageUrl={productionPageUrl}
			previewContainerRef={previewContainerRef}
			previewCss={previewCss}
			editorComponents={basePreviewComponents}
			previewComponents={previewComponents}
			previewWidth={previewWidth}
			previewContainerWidth={previewContainerWidth}
			runtimeConfig={runtimeConfig}
			allowImageVariables={allowImageVariables}
			activeComponentKey={activeComponentKey}
			setActiveComponentKey={setActiveComponentKey}
			updateComponentValue={updateComponentValue}
			removeComponent={removeComponent}
			moveComponent={moveComponent}
			duplicateComponent={duplicateComponent}
			onWrapInContainer={wrapInContainer}
			onUnwrapContainer={unwrapContainer}
			onUndo={componentHistory.undo}
			onRedo={componentHistory.redo}
			canUndo={componentHistory.canUndo}
			canRedo={componentHistory.canRedo}
			historyEntries={componentHistory.entries}
			historyIndex={componentHistory.currentIndex}
			onJumpHistory={componentHistory.jumpTo}
			deployLinks={deployLinks}
			onInsertComponentAt={openLibraryAtGap}
			activeComponentIndex={activeComponentIndex}
			onSelectComponentFromPreview={handleSelectComponentFromPreview}
			showLibrary={showLibrary}
			setShowLibrary={setShowLibrary}
			libraryPicker={libraryPicker}
			componentDefinitionMap={componentDefinitionMap}
			addComponentFromDefinition={addComponentFromDefinition}
			markdownInsertRef={markdownInsertRef}
			pageJsonDraft={pageJsonDraft}
			pageJsonError={pageJsonError}
			handleFormatJson={handleFormatJson}
			handlePageJsonChange={handlePageJsonChange}
			jsonEditorOptions={jsonEditorOptions}
			isJsonLocked={isJsonLocked}
			onSelectBlocks={() => setContentViewMode("blocks")}
			onSelectJson={() => setContentViewMode("json")}
			onSelectMarkdown={() => {
				const latestDraft = draftRef.current ?? draft;
				const md = pageToEditableMarkdown(latestDraft, initialLanguage ?? "fr");
				markdownDraftRef.current = md;
				setMarkdownDraft(md);
				setMarkdownError(null);
				setContentViewMode("markdown");
			}}
			markdownDraft={markdownDraft}
			markdownError={markdownError}
			onMarkdownChange={handleMarkdownChange}
			onShowConnections={() => setConnectionsOpen(true)}
			onShowReachability={() => setReachabilityOpen(true)}
			connectionsDisabled={connectionsDisabled}
			publishStatus={effectivePublishStatus}
			publishContentId={draft?.id ?? null}
			publishDisabled={publishDisabled}
			publishHidden={hasUnsavedChanges}
			onPublished={() => {
				reloadCurrentPage();
				setDeploymentQueueRefresh((v) => v + 1);
			}}
			onPublishingChange={setPublishingProduction}
			/* --- Shared secondary bar --- */
			onAnalyze={() => setShowPageAnalyze(true)}
			translateHref={translateHref}
			onCreateTicket={draft?.id ? () => setShowTicket(true) : undefined}
			onSelectPageFromTree={(node) => {
				const nodeId = node?.id ?? null;
				const nodeSlug = node?.slug ?? node?.path ?? null;
				const nodeLanguage = (node?.language ?? draft?.language ?? initialLanguage ?? DEFAULT_LANGUAGE).toString();
				if (nodeId) {
					history.push(`/page/${encodeURIComponent(nodeId)}`);
					return;
				}
				if (nodeSlug) {
					history.push(`/page/${encodeURIComponent(nodeLanguage)}/${nodeSlug}`);
				}
			}}
			connectionsOpen={connectionsOpen}
			setConnectionsOpen={setConnectionsOpen}
			initialLanguage={initialLanguage}
			pageNavOpen={pageNavOpen}
			setPageNavOpen={setPageNavOpen}
			isPageDeleted={draft?.isDeleted === true}
			onRestore={handleRestore}
			restoring={restoring}
		/>
		<ReachabilityModal open={reachabilityOpen} contentId={draft?.id ?? null} onClose={() => setReachabilityOpen(false)} />
		<AIAnalyzeModal
			show={showPageAnalyze}
			onClose={() => setShowPageAnalyze(false)}
			schemaKey="page"
			language={draft?.language ?? "fr"}
			contentId={draft?.id ?? undefined}
			slug={draft?.slug ?? undefined}
			data={{
				title: draft?.title ?? "",
				slug: draft?.slug ?? "",
				metadata: draft?.metadata ?? {},
				components: draft?.components ?? []
			}}
		/>
		</>
	);
};

export default PageEditor;
